import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowLeft,
  ArrowRight,
  MessageCircleHeart,
  BarChart3,
  CalendarHeart,
  Users2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/tu9m9qqv_Logo.png";
const PALETTE =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/lopuxfok_Brand%20Colors.png";
const MOCK_GRID =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/rbjz2wot_mock2.jpg";

const PINK = "#F82A70";
const MINT = "#2CCBBC";
const BLUSH = "#FFF3F6";
const INK = "#111111";

// SaySo walkthrough — user-uploaded demo recording of the final prototype.
const SAYSO_VIDEO =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_portfolio-pro-1623/artifacts/59da9577ad40bdf5_SAYSO.mp4";

const palette = [
  { name: "Mint Fresh", hex: "#2CCBBC", text: "#fff" },
  { name: "Girl Pink", hex: "#F82A70", text: "#fff" },
  { name: "Soft Blush", hex: "#FFF3F6", text: "#111" },
  { name: "Diary Ink", hex: "#111111", text: "#fff" },
];

const features = [
  {
    icon: MessageCircleHeart,
    title: "Ask for advice",
    body:
      "Post a question to the community or just your close friends — anonymous or under your name. Get thoughtful, girl-to-girl advice instead of scattered replies across five group chats.",
    bg: PINK,
  },
  {
    icon: BarChart3,
    title: "Text & image polls",
    body:
      "Can’t decide between two outfits, two bags, two dates? Drop a two-photo poll and let the girls vote. Real-time results, no popularity contest.",
    bg: MINT,
  },
  {
    icon: CalendarHeart,
    title: "Make a plan",
    body:
      "Turn a group chat idea into an actual event — brunch dates, wine & paint nights, movie marathons. Everyone RSVPs in one place, no more Google-Docs plans.",
    bg: PINK,
  },
  {
    icon: ShieldCheck,
    title: "Privacy first",
    body:
      "Close friends, wider community or anonymous — you choose who sees what, every single post. No followers, no algorithm, no chase for popularity.",
    bg: MINT,
  },
  {
    icon: Users2,
    title: "Communities",
    body:
      "Small circles built around what you actually care about — relationships, fashion, career, mental health. Support you can trust, not a public feed to perform for.",
    bg: PINK,
  },
  {
    icon: Sparkles,
    title: "For You feed",
    body:
      "A gentle mix of trending topics, your friends’ latest questions and events coming up — curated to feel like a diary you actually want to open.",
    bg: MINT,
  },
];

const marqueeWords = [
  "SHARE",
  "ASK",
  "PLAN",
  "ALL THINGS GIRL",
  "SAFE SPACE",
  "GIRLS ONLY",
  "SAY IT",
];

// Scrapbook doodles — hearts, stars, flowers, squiggles floating around
function SaysoDoodles({ variant = "hero" }) {
  const items =
    variant === "hero"
      ? [
          { emoji: "♡", top: "8%", left: "6%", size: 40, r: -14, color: PINK },
          { emoji: "✿", top: "18%", right: "10%", size: 44, r: 12, color: MINT },
          { emoji: "★", top: "62%", left: "4%", size: 30, r: -6, color: MINT },
          { emoji: "♡", top: "72%", right: "6%", size: 38, r: 8, color: PINK },
          { emoji: "✎", top: "38%", left: "12%", size: 26, r: 20, color: INK },
          { emoji: "❋", top: "48%", right: "18%", size: 32, r: -18, color: PINK },
        ]
      : [
          { emoji: "★", top: "10%", left: "8%", size: 26, r: -8, color: MINT },
          { emoji: "♡", top: "24%", right: "12%", size: 32, r: 14, color: PINK },
          { emoji: "✿", top: "62%", left: "6%", size: 30, r: -12, color: PINK },
          { emoji: "❋", top: "78%", right: "14%", size: 28, r: 10, color: MINT },
        ];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {items.map((d, i) => (
        <span
          key={i}
          className="absolute font-bold"
          style={{
            top: d.top,
            left: d.left,
            right: d.right,
            fontSize: `${d.size}px`,
            color: d.color,
            transform: `rotate(${d.r}deg)`,
            opacity: 0.9,
          }}
        >
          {d.emoji}
        </span>
      ))}
      {/* Scribble squiggle svgs */}
      <svg
        className="absolute"
        style={{ top: "34%", right: "6%", width: "160px", opacity: 0.6 }}
        viewBox="0 0 160 40"
        fill="none"
      >
        <path
          d="M4 20 Q 20 4, 36 20 T 68 20 T 100 20 T 156 20"
          stroke={PINK}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      <svg
        className="absolute"
        style={{ bottom: "12%", left: "22%", width: "140px", opacity: 0.55 }}
        viewBox="0 0 140 40"
        fill="none"
      >
        <path
          d="M4 20 Q 20 4, 36 20 T 68 20 T 100 20 T 136 20"
          stroke={MINT}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

// Washi tape stripe with hearts / stripes pattern
function WashiTape({ color = PINK, rotate = -4, className = "" }) {
  return (
    <span
      className={`absolute h-6 w-24 ${className}`}
      style={{
        background: color,
        transform: `rotate(${rotate}deg)`,
        opacity: 0.85,
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0, rgba(255,255,255,0.35) 6px, transparent 6px, transparent 12px)",
      }}
    />
  );
}

function SaysoMarquee({ bg = INK, color = PINK }) {
  return (
    <div
      className="py-4 border-y-4 border-[#111] overflow-hidden"
      style={{ background: bg }}
      data-testid="sayso-marquee"
    >
      <Marquee speed={55} gradient={false} autoFill>
        {marqueeWords.map((w) => (
          <span
            key={w}
            className="mx-6 font-extrabold text-xl md:text-2xl uppercase tracking-wide flex items-center gap-6"
            style={{ color }}
          >
            {w} <span style={{ color: color === PINK ? MINT : PINK }}>♡</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export default function CaseSayso() {
  return (
    <main
      className="min-h-screen text-[#111] font-kickz"
      style={{ background: BLUSH }}
      data-testid="case-sayso-page"
    >
      {/* Nav */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md border-b-4 border-[#111]"
        style={{ background: "rgba(255, 243, 246, 0.92)" }}
      >
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-bold text-sm uppercase tracking-wide hover:text-[color:var(--sayso-pink)] transition-colors"
            style={{ ["--sayso-pink"]: PINK }}
            data-testid="sayso-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>
          <img
            src={LOGO}
            alt="SaySo logo"
            className="h-10 md:h-12 w-auto"
            data-testid="sayso-nav-logo"
          />
          <Link
            to="/work/the-present"
            className="hidden md:flex items-center gap-2 font-bold text-sm uppercase tracking-wide hover:text-[#F82A70] transition-colors"
            data-testid="sayso-next-link"
          >
            Next project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: BLUSH }}
      >
        <SaysoDoodles variant="hero" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 pt-16 pb-14 md:pt-24 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <div className="flex flex-wrap gap-3 mb-8">
              {["Mobile App", "Social", "Women-Centered", "2026"].map((t, i) => (
                <span
                  key={t}
                  className="px-4 py-1.5 rounded-full text-white text-xs font-bold uppercase tracking-wider hover:scale-110 hover:-rotate-3 transition-transform duration-300 cursor-default"
                  style={{
                    background: i % 2 ? MINT : PINK,
                    rotate: `${(i % 2 ? 1 : -1) * 1.5}deg`,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
            <motion.img
              initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ duration: 0.9, delay: 0.15, type: "spring" }}
              whileHover={{ rotate: 2, scale: 1.04 }}
              src={LOGO}
              alt="SaySo logo"
              className="w-[min(360px,70vw)]"
              data-testid="sayso-hero-logo"
            />
            <h1 className="mt-8 text-4xl md:text-6xl font-extrabold leading-[1.05] max-w-[16ch]">
              Share. Ask. Plan.{" "}
              <span
                className="relative inline-block"
                style={{ color: PINK }}
              >
                All things girl.
                <span
                  className="absolute left-0 right-0 -bottom-2 h-1.5 rounded-full"
                  style={{ background: MINT }}
                />
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#333] max-w-[46ch]">
              A private, women-centered social space — somewhere between a
              group chat, a diary and a supportive community. Ask, poll, plan,
              vote. No followers. No chase for likes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-6 relative flex justify-center"
          >
            {/* iPhone frame — compact scale to match K!ckz hero mockup presence */}
            <div
              className="relative"
              style={{
                padding: "12px",
                borderRadius: "48px",
                background:
                  "linear-gradient(180deg,#1a1a1a 0%,#0a0a0a 55%,#1a1a1a 100%)",
                boxShadow:
                  "inset 0 0 0 2px #2a2a2a, 0 30px 60px -20px rgba(0,0,0,0.55)",
              }}
              data-testid="sayso-iphone-frame"
            >
              {/* Screen — 320 × 693 (compact iPhone ratio) */}
              <div
                className="relative overflow-hidden bg-black"
                style={{
                  width: "320px",
                  height: "693px",
                  borderRadius: "36px",
                }}
              >
                <video
                  src={SAYSO_VIDEO}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="absolute inset-0 w-full h-full object-cover block"
                  style={{ background: "#FFF3F6" }}
                  data-testid="sayso-walkthrough-video"
                />
                {/* Dynamic island */}
                <div
                  className="absolute z-10 pointer-events-none"
                  style={{
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "88px",
                    height: "26px",
                    background: "#000",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  }}
                />
              </div>
              {/* Side buttons */}
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: "-3px",
                  top: "22%",
                  width: "3px",
                  height: "30px",
                  background: "#2a2a2a",
                  borderRadius: "2px",
                }}
              />
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: "-3px",
                  top: "32%",
                  width: "3px",
                  height: "54px",
                  background: "#2a2a2a",
                  borderRadius: "2px",
                }}
              />
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: "-3px",
                  top: "44%",
                  width: "3px",
                  height: "54px",
                  background: "#2a2a2a",
                  borderRadius: "2px",
                }}
              />
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  right: "-3px",
                  top: "34%",
                  width: "3px",
                  height: "80px",
                  background: "#2a2a2a",
                  borderRadius: "2px",
                }}
              />
            </div>

            {/* Caption chip */}
            <span
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#111] bg-white text-[#111] font-bold text-xs uppercase tracking-widest"
              data-testid="sayso-video-caption"
            >
              ♡ Prototype walkthrough — on loop
            </span>
          </motion.div>
        </div>

        {/* Meta */}
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 pb-14 md:pb-20 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Role", "UI/UX & Brand Design"],
            ["Year", "2026"],
            ["Audience", "Women, 16–30"],
            ["Deliverables", "App design, logo, brand"],
          ].map(([k, v]) => (
            <div
              key={k}
              className="rounded-2xl border-4 border-[#111] bg-white p-4 hover:-rotate-1 hover:scale-[1.02] transition-transform duration-300"
              style={{ boxShadow: "6px 6px 0 #111" }}
            >
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#666]">
                {k}
              </div>
              <div className="mt-1 font-bold text-sm md:text-base">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Marquee */}
      <SaysoMarquee bg={INK} color={PINK} />

      {/* Mockup grid */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: PINK }}
      >
        <SaysoDoodles variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <motion.img
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            src={MOCK_GRID}
            alt="SaySo app — home, create, profile, planner"
            className="w-full rounded-[32px] border-4 border-[#111] shadow-[12px_12px_0_#111]"
            data-testid="sayso-mockup-grid"
          />
        </div>
      </section>

      {/* Brief */}
      <section className="py-20 md:py-28" style={{ background: BLUSH }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span
              className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-extrabold uppercase tracking-wider"
              style={{ background: MINT }}
            >
              The Brief
            </span>
          </div>
          <div className="md:col-span-8">
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">
              A safe space to ask, share and{" "}
              <span style={{ color: PINK }}>decide together.</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#444]">
              Girls already ask their friends everything — outfits, dates,
              breakups, career moves — but the conversation is scattered across
              five group chats, three DMs and someone’s notes app. SaySo pulls
              all of it into one calm, feminine space: post a question, run a
              two-image poll, plan the brunch, and hear the girls out.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-[#444]">
              Unlike traditional social media, SaySo removes followers and the
              popularity chase. It’s built around trust, privacy and small
              circles — the vibe of a group chat, the depth of a diary, the
              feel of a community that has your back.
            </p>
          </div>
        </div>
      </section>

      {/* From idea to final flow — narrative */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: "#FFFFFF" }}
        data-testid="sayso-journey-section"
      >
        <SaysoDoodles variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span
                className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-extrabold uppercase tracking-wider"
                style={{ background: INK }}
              >
                The journey
              </span>
              <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight max-w-[22ch]">
                From a scattered group chat to a{" "}
                <span style={{ color: PINK }}>safe little world.</span>
              </h2>
            </div>
            <p className="max-w-[46ch] text-[#444] leading-relaxed md:text-right">
              A short walk-through of how SaySo takes a girl from “I don’t know
              who to ask” all the way to a plan, a poll result and a soft
              community that answers back.
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                step: "01",
                title: "Soft onboarding",
                body:
                  "Open the app, pick a vibe, add your close friends. No followers, no bios, no popularity score — just a cozy entry point that feels like opening a diary, not a feed.",
                bg: BLUSH,
              },
              {
                step: "02",
                title: "Ask the question",
                body:
                  "Type the thing you’d usually dump in five group chats — a dating dilemma, an outfit panic, a career pivot. Choose who sees it: close circle, community, or fully anonymous.",
                bg: "#FFE2EC",
              },
              {
                step: "03",
                title: "Poll it out",
                body:
                  "Can’t decide? Drop two photos, two options, two paths. Live results stream in from the girls — honest, kind, no screenshots to other chats.",
                bg: "#D7F7F2",
              },
              {
                step: "04",
                title: "Make the plan",
                body:
                  "Turn an idea into an actual event — brunch, paint night, movie marathon. Everyone RSVPs in one place, no half-dead Google Docs or ignored calendar invites.",
                bg: BLUSH,
              },
              {
                step: "05",
                title: "Lean on the community",
                body:
                  "Small, interest-based circles — relationships, mental health, fashion, career. Threads stay respectful by design: slower replies, longer answers, zero algorithm.",
                bg: "#FFE2EC",
              },
              {
                step: "06",
                title: "The final result",
                body:
                  "A phone that doesn’t drain you. A quieter social space where every post leaves you a little clearer than before — and a girl who feels, finally, heard.",
                bg: "#D7F7F2",
              },
            ].map((s, i) => (
              <motion.li
                key={s.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                whileHover={{ rotate: i % 2 ? 1 : -1, scale: 1.015 }}
                className="relative rounded-[24px] border-4 border-[#111] p-7 shadow-[8px_8px_0_#111]"
                style={{ background: s.bg }}
                data-testid={`sayso-journey-step-${s.step}`}
              >
                <WashiTape
                  color={i % 2 ? MINT : PINK}
                  rotate={i % 2 ? 6 : -6}
                  className="-top-3 left-6"
                />
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-2xl md:text-3xl font-extrabold leading-tight max-w-[18ch]">
                    {s.title}
                  </h3>
                  <span
                    className="font-mono text-sm font-bold px-3 py-1 rounded-full bg-white border-2 border-[#111]"
                    style={{ color: INK }}
                  >
                    {s.step}
                  </span>
                </div>
                <p className="mt-4 text-[#333] leading-relaxed">{s.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* Marquee 2 */}
      <SaysoMarquee bg={MINT} color={INK} />

      {/* Features */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: BLUSH }}
      >
        <SaysoDoodles variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span
                className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-extrabold uppercase tracking-wider"
                style={{ background: PINK }}
              >
                What it does
              </span>
              <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight max-w-[20ch]">
                Every app on your phone — <br /> in one soft, safe corner.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                whileHover={{ rotate: i % 2 ? 1.4 : -1.4, scale: 1.02 }}
                className="relative rounded-[28px] border-4 border-[#111] p-7 shadow-[8px_8px_0_#111] bg-white"
                data-testid={`sayso-feature-${i}`}
              >
                <WashiTape
                  color={i % 2 ? MINT : PINK}
                  rotate={i % 2 ? 8 : -8}
                  className="-top-3 left-6"
                />
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: f.bg }}
                >
                  <f.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="mt-5 text-2xl font-extrabold">{f.title}</h3>
                <p className="mt-3 text-[#444] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design language */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: MINT }}
      >
        <SaysoDoodles variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <span
            className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-extrabold uppercase tracking-wider"
            style={{ background: PINK }}
          >
            Design language
          </span>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 gap-4">
                {palette.map((c, i) => (
                  <motion.div
                    key={c.hex}
                    whileHover={{ rotate: i % 2 ? 2 : -2, scale: 1.04 }}
                    className="rounded-2xl border-4 border-[#111] p-6 h-32 flex flex-col justify-between"
                    style={{ background: c.hex, color: c.text }}
                    data-testid={`sayso-swatch-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="font-extrabold">{c.name}</span>
                    <span className="font-mono text-sm opacity-80">
                      {c.hex}
                    </span>
                  </motion.div>
                ))}
              </div>
              <p className="mt-6 text-white/95 leading-relaxed max-w-[52ch]">
                Blush pink backgrounds, hot pink accents and turquoise
                highlights carry the whole app. Torn paper edges, washi tape,
                doodled hearts, stars and flowers layer on top — the vibe of a
                Gen-Z scrapbook, not a social network.
              </p>
            </div>
            <div className="md:col-span-5 bg-white rounded-[28px] border-4 border-[#111] p-8 shadow-[8px_8px_0_#111]">
              <div
                className="text-[80px] leading-none"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                Aa<span style={{ color: PINK }}>.</span>
              </div>
              <div
                className="mt-4 text-xl"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                Poppins — friendly, rounded, girly.
              </div>
              <p
                className="mt-3 text-[#444] leading-relaxed"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
              >
                Poppins carries the entire interface — soft rounded terminals,
                geometric shapes and a warm, approachable rhythm. Headlines run
                semi-bold (600), body copy stays light (400) so posts,
                questions and polls feel like handwriting in a group chat, not
                a wall of app text.
              </p>
              <div
                className="mt-6 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-widest text-[#666]"
              >
                <span className="px-2 py-1 border border-[#111] rounded-full">
                  Poppins 600
                </span>
                <span className="px-2 py-1 border border-[#111] rounded-full">
                  Poppins 500
                </span>
                <span className="px-2 py-1 border border-[#111] rounded-full">
                  Poppins 400
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo love */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: BLUSH }}
      >
        <SaysoDoodles variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span
              className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-extrabold uppercase tracking-wider"
              style={{ background: INK }}
            >
              The mark
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight">
              A pair of lips saying{" "}
              <span style={{ color: PINK }}>‘Say So’.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#444]">
              The logo is a hand-drawn pair of lips holding the wordmark inside
              — feminine, confident, a little bit unbothered. The single-line
              stroke keeps it flexible: tap it on the app icon, stamp it on
              stickers, letter it big on a t-shirt.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-[#444]">
              The name is a nod to permission — <em>“say so”</em> as in speak
              up, share your opinion, own your voice. That’s what the whole
              product is about.
            </p>
          </div>
          <div className="md:col-span-6 flex justify-center">
            <motion.div
              whileHover={{ rotate: 3, scale: 1.03 }}
              className="relative rounded-[32px] border-4 border-[#111] p-10 md:p-14 shadow-[12px_12px_0_#111]"
              style={{ background: "#FFFFFF" }}
            >
              <WashiTape color={PINK} rotate={-8} className="-top-3 left-8" />
              <WashiTape color={MINT} rotate={10} className="-top-3 right-8" />
              <img
                src={LOGO}
                alt="SaySo primary logo"
                className="w-[min(360px,70vw)] mx-auto"
                data-testid="sayso-mark-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer / next */}
      <footer className="py-16" style={{ background: INK }}>
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-white hover:text-[#F82A70] transition-colors"
            data-testid="sayso-footer-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all work
          </Link>
          <Link
            to="/work/the-present"
            className="group flex items-center gap-4 text-white"
            data-testid="sayso-footer-next"
          >
            <span
              className="text-2xl md:text-4xl font-extrabold group-hover:text-[#F82A70] transition-colors"
            >
              Next: The Present
            </span>
            <ArrowRight className="w-8 h-8 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </div>
      </footer>
    </main>
  );
}
